import { ReactNode, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface ModalProps {
  title?: string
  subtitle?: string
  footer?: ReactNode
  isOpen: boolean
  onClose: (_cancelled: boolean) => void
  size?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  modalClassName?: string
  showDialogAlert?: boolean
  confirmOnClose?: boolean // Nueva prop para controlar confirmación
  showDefaultDescription?: boolean // Si false y no hay subtitle, no muestra texto por defecto
}

const sizesClassName = {
  ['xxs']: 'sm:max-w-xs md:max-w-sm lg:max-w-md',
  ['xs']: 'sm:max-w-md md:max-w-lg lg:max-w-xl',
  ['s']: 'sm:max-w-lg md:max-w-xl lg:max-w-2xl',
  ['m']: 'sm:max-w-xl md:max-w-2xl lg:max-w-3xl',
  ['l']: 'sm:max-w-2xl md:max-w-3xl lg:max-w-4xl',
  ['xl']: 'sm:max-w-3xl md:max-w-4xl lg:max-w-5xl',
  ['xxl']: 'sm:max-w-4xl md:max-w-5xl lg:max-w-6xl',
}

export const Modal = ({
  title,
  subtitle,
  footer,
  isOpen,
  onClose,
  size = 'l',
  modalClassName,
  children,
  confirmOnClose = false,
  showDefaultDescription = true,
}: ModalProps & { children: ReactNode }) => {
  const [showConfirm, setShowConfirm] = useState(false)

  // Handler para el cierre (cruz o fondo)
  const handleRequestClose = () => {
    if (confirmOnClose) {
      setShowConfirm(true)
    } else {
      onClose(false)
    }
  }

  return (
    <>
      <Dialog
        onOpenChange={(state) => {
          if (!state) handleRequestClose()
        }}
        open={isOpen}
      >
        <DialogContent
          aria-describedby={
            subtitle || showDefaultDescription ? 'modal-description' : undefined
          }
          className={`flex ${footer ? 'h-fit' : 'h-fit max-h-[90dvh]'} flex-col ${sizesClassName[size]} ${
            modalClassName ?? ''
          }`}
        >
          <DialogHeader className="flex-shrink-0">
            {title && (
              <DialogTitle className="text-xl md:text-2xl">{title}</DialogTitle>
            )}
            {(subtitle || showDefaultDescription) && (
              <DialogDescription
                className="text-sm text-muted-foreground"
                id="modal-description"
              >
                {subtitle ||
                  (title
                    ? `Configuración para ${title}`
                    : 'Configuración del modal') ||
                  'Modal de configuración'}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className={footer ? 'min-h-0 flex-1' : ''}>{children}</div>
          {footer && (
            <DialogFooter className="flex-shrink-0 flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-between">
              {footer}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {/* Diálogo de confirmación al cerrar */}
      <AlertDialog open={showConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar el proceso?</AlertDialogTitle>
            <AlertDialogDescription>
              Si cancela ahora, perderá todos los datos ingresados. ¿Está seguro
              de que desea cancelar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirm(false)}>
              Continuar editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowConfirm(false)
                onClose(false)
              }}
            >
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
